import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import { UrlSegment as ProffeoUrlSegment } from '@app/shared/types/enums/url-segment.enum';
import { Observable, of } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const redirectUrl = state.url === `/${ProffeoUrlSegment.SIGN_OUT}` ? '/' : state.url;
    return this._check(redirectUrl);
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  public canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === `/${ProffeoUrlSegment.SIGN_OUT}` ? '/' : state.url;
    return this._check(redirectUrl);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  public canLoad(_route: Route, _segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    void _route;
    void _segments;
    return this._check('/');
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @param redirectUrl
   * @private
   */
  private _check(redirectUrl: string): Observable<boolean> {
    const isAuthenticated = this.authService.isAuthenticated();

    if (!isAuthenticated) {
      console.log('[AuthGuard] User not authenticated, redirect to sign in');

      if (this.router.url.includes(`/${ProffeoUrlSegment.SIGN_IN}`)) {
        return of(true);
      }

      void this.router.navigate([ProffeoUrlSegment.SIGN_IN], {
        queryParams: { redirectUrl }
      });

      // Prevent the access
      return of(false);
    }

    // Allow the access
    return of(true);
  }
}
